import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { ConfirmationToken } from '@stripe/stripe-js';
import { useStripe } from '@stripe/react-stripe-js';
import { useIntl } from 'react-intl';
import {
  completeOrderSteppedCheckout,
  savePaymentMethodData,
} from '../../slices/payments';
import store from '../../store';
import {
  CARD_TOKEN_ERROR,
  PAYMENT_BLOCK,
} from '../../blocks/Checkout/SteppedCheckout/constants';

import {
  setBillingAddress,
  setShippingAddressSteppedCheckout,
} from '../../slices/cart';
import {
  getAvailableShippingMethods,
  setShippingMethodsForAllProductsInCart,
} from '../../slices/shippingMethods';
import { CartInStore } from '../../slices/cart/types';
import {
  GetShippingMethodsResponse,
  SetShippingMethodsEntry,
} from '../../slices/shippingMethods/types';
import { IN_HOME_SETUP_FREE_SHIPPING_METHOD_KEY } from '../../utils/common/constants';

const useStripeExpress = ({
  stripePaymentIntentIdFromUrl,
  stripePaymentIntentClientSecretFromUrl,
  stripeIsExpressButton,
}: {
  stripePaymentIntentIdFromUrl?: string;
  stripePaymentIntentClientSecretFromUrl?: string;
  stripeIsExpressButton?: string;
}) => {
  const [shouldPlaceOrder, setShouldPlaceOrder] = useState<boolean>(false);

  const dispatch = useDispatch();
  const { locale } = useIntl();

  const stripe = useStripe();

  useEffect(() => {
    if (stripe && stripePaymentIntentClientSecretFromUrl && !shouldPlaceOrder) {
      stripe
        .retrievePaymentIntent(stripePaymentIntentClientSecretFromUrl)
        .then((paymentIntentResult) => {
          if (
            paymentIntentResult.paymentIntent?.status === 'requires_capture'
          ) {
            setShouldPlaceOrder(true);
          } else {
            dispatch({
              type: 'addError',
              errorBlock: PAYMENT_BLOCK,
              errorCode: CARD_TOKEN_ERROR,
            });
          }
        });
    }
  }, [
    dispatch,
    stripe,
    stripePaymentIntentClientSecretFromUrl,
    shouldPlaceOrder,
  ]);

  useEffect(() => {
    const processStripeExpressInfo = async () => {
      const stripeConfirmation = localStorage.getItem('stripe_confirmation');
      if (stripeConfirmation) {
        // This is JSON already.
        dispatch({
          type: 'setPaymentToken',
          token: stripeConfirmation,
        });
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const confirmationTokenResult: ConfirmationToken =
          JSON.parse(stripeConfirmation);

        const [first, ...rest] = (
          confirmationTokenResult.payment_method_preview?.billing_details
            ?.name ?? ' '
        ).split(' ');
        const last = rest.join(' ');

        const shippingAddressToAssign = {
          streetAddress: confirmationTokenResult.shipping?.address?.line1 ?? '',
          city: confirmationTokenResult.shipping?.address?.city ?? '',
          postalCode:
            confirmationTokenResult.shipping?.address?.postal_code ?? '',
          country: confirmationTokenResult.shipping?.address?.country ?? '',
          state: confirmationTokenResult.shipping?.address?.state ?? '',
          additionalStreetInfo:
            confirmationTokenResult.shipping?.address?.line2 ?? '',
          email:
            confirmationTokenResult.payment_method_preview.billing_details
              .email ?? '',
          phone:
            confirmationTokenResult.payment_method_preview.billing_details
              .phone ?? '',
          firstName: first ?? '',
          lastName: last ?? '',
        };
        // eslint-disable-next-line @typescript-eslint/await-thenable
        await dispatch(
          setShippingAddressSteppedCheckout({
            shippingAddress: shippingAddressToAssign,
          }),
        );
        // eslint-disable-next-line @typescript-eslint/await-thenable
        await dispatch(
          setBillingAddress({
            billingAddress: shippingAddressToAssign,
          }),
        );

        // eslint-disable-next-line @typescript-eslint/await-thenable
        await dispatch(
          getAvailableShippingMethods({
            locale,
            overwritePaidInHomeSetupShipping: false,
            customInHomeSetupPricing: false,
          }),
        );

        const { shippingMethods } = store.getState() as {
          cart: CartInStore;
          shippingMethods: {
            availableShippingMethods: GetShippingMethodsResponse;
          };
        };

        const { availableShippingMethods } = shippingMethods;
        // eslint-disable-next-line no-console
        console.log(
          'Stripe - availableShippingMethods',
          availableShippingMethods,
        );

        const shippingMethodsToAssign: SetShippingMethodsEntry[] =
          availableShippingMethods.map((shippingMethod) => ({
            parentKey: shippingMethod.key,
            groups: shippingMethod.groups.map((group) => {
              const isInHomeSetupFreeEligible = !!group.shippingMethods.find(
                (method) =>
                  method.key === IN_HOME_SETUP_FREE_SHIPPING_METHOD_KEY,
              );

              if (isInHomeSetupFreeEligible) {
                return {
                  key: IN_HOME_SETUP_FREE_SHIPPING_METHOD_KEY,
                  name: 'In-Home Setup',
                  cost: 0,
                  needsRemoval: true,
                  skus: group.products.map((product) => product.sku),
                };
              }

              return {
                key: 'FEDEX_GROUND',
                name: 'Standard Shipping',
                cost: 0,
                needsRemoval: false,
                skus: group.products.map((product) => product.sku),
              };
            }),
          }));

        // eslint-disable-next-line @typescript-eslint/await-thenable
        dispatch(
          setShippingMethodsForAllProductsInCart({
            shippingMethods: shippingMethodsToAssign,
            overwritePaidInHomeSetupShipping: false,
            customInHomeSetupPricing: false,
          }),
        );

        dispatch(
          savePaymentMethodData({
            id: confirmationTokenResult?.id,
            paymentData: confirmationTokenResult?.payment_method_preview,
          }),
        );

        dispatch({ type: 'setPaymentStatus', status: 'succeeded' });

        // We have processed the express confirmation token, and have no need
        // for this anymore. Additionally, we remove this so if the user changes
        // payment methods, it breaks out of any express flow.
        localStorage.removeItem('stripe_confirmation');
        localStorage.removeItem('expressShippingAddress');
      }
    };
    if (stripe && stripeIsExpressButton) {
      processStripeExpressInfo();
    }
  }, [dispatch, locale, stripe, stripeIsExpressButton]);

  useEffect(() => {
    const placeStripeOrder = async () => {
      dispatch({
        type: 'order/setStatus',
        payload: 'loading',
      });

      // eslint-disable-next-line no-console
      console.log(
        'Stripe - Payment intent succeeded:',
        stripePaymentIntentIdFromUrl,
      );
      // eslint-disable-next-line no-console
      console.log(
        'Stripe - Client secret:',
        stripePaymentIntentClientSecretFromUrl,
      );
      const { paymentMethodData: updatedPaymentMethodData } = store.getState()
        .payments as {
        paymentMethodData?: Record<string, string>;
      };

      dispatch(
        savePaymentMethodData({
          ...updatedPaymentMethodData,
          id: stripePaymentIntentIdFromUrl,
        }),
      );
      // eslint-disable-next-line @typescript-eslint/await-thenable
      await dispatch(completeOrderSteppedCheckout());
    };

    if (shouldPlaceOrder && stripe) {
      placeStripeOrder();
    }
  }, [
    dispatch,
    shouldPlaceOrder,
    stripe,
    stripePaymentIntentClientSecretFromUrl,
    stripePaymentIntentIdFromUrl,
  ]);

  return null;
};

export default useStripeExpress;
